import Swiper from "swiper";
import { Navigation, Thumbs, A11y } from "swiper/modules";
import {
  createIcons,
  Home, ChevronRight, CheckCircle2, Clock, Check, Loader2,
  MapPin, Calendar, Tags, ChevronLeft, ChevronRight as ChevronRightIcon,
  FileText, LayoutGrid, X, Maximize2, ZoomIn
} from "lucide";

import lightGallery from "lightgallery";
import lgZoom from "lightgallery/plugins/zoom";
import lgFullscreen from "lightgallery/plugins/fullscreen";

export class ProjectsTabs {
  constructor() {
    this.root = document.querySelector(".js-projects-index");
    if (!this.root) return;

    createIcons({
      icons: {
        Home, ChevronRight, CheckCircle2, Clock,
        Check, Loader2, MapPin, Calendar, Tags
      }
    });

    const tabs = this.root.querySelectorAll(".projects-tabs__btn");
    const panels = this.root.querySelectorAll(".projects-panel");

    tabs.forEach(btn => {
      btn.addEventListener("click", () => {
        const target = btn.dataset.target;
        tabs.forEach(b => {
          b.classList.toggle("is-active", b === btn);
          b.setAttribute("aria-selected", String(b === btn));
        });
        panels.forEach(p => {
          const isTarget = `#${p.id}` === target;
          p.classList.toggle("is-active", isTarget);
          p.hidden = !isTarget;
        });
      });
    });
  }
}

export class ProjectGallery {
  constructor() {
    window.addEventListener("load", () => this.init());
  }

  async init() {
    this.mainEl = document.querySelector(".js-project-gallery");
    this.thumbsEl = document.querySelector(".js-project-thumbs");
    if (!this.mainEl || !this.thumbsEl) return;

    createIcons({
      icons: { Home, ChevronRight, LayoutGrid, ChevronLeft, ChevronRightIcon, MapPin, Calendar, Tags, FileText }
    });

    await Promise.all([
      this.waitForImages(this.mainEl),
      this.waitForImages(this.thumbsEl),
      this.nextFrame()
    ]);

    this.thumbs = new Swiper(this.thumbsEl, {
      modules: [A11y],
      slidesPerView: 4,
      spaceBetween: 10,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      observer: true,
      observeParents: true,
      breakpoints: { 320: { slidesPerView: 3 }, 640: { slidesPerView: 4 } }
    });

    this.main = new Swiper(this.mainEl, {
      modules: [Navigation, Thumbs, A11y],
      slidesPerView: 1,
      speed: 500,
      navigation: {
        nextEl: ".js-project-next",
        prevEl: ".js-project-prev",
      },
      thumbs: { swiper: this.thumbs },
      observer: true,
      observeParents: true,
      watchOverflow: true,
      autoHeight: false
    });

    this.safeUpdate();

    this.ro = new ResizeObserver(() => this.safeUpdate());
    this.ro.observe(this.mainEl);

    this.initLightGallery();
  }

  initLightGallery() {
    const wrapper = this.mainEl.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    lightGallery(wrapper, {
      selector: ".swiper-slide img",
      plugins: [lgZoom, lgFullscreen],
      download: false,
      zoom: true,
      fullscreen: true,
      mobileFirst: true
    });

    wrapper.addEventListener("lgAfterOpen", () => {
      const closeBtn = document.querySelector(".lg-close");
      if (closeBtn && !closeBtn.querySelector("i")) {
        closeBtn.innerHTML = `<i data-lucide="x"></i>`;
      }

      const fullscreenBtn = document.querySelector(".lg-fullscreen");
      if (fullscreenBtn && !fullscreenBtn.querySelector("i")) {
        fullscreenBtn.innerHTML = `<i data-lucide="maximize-2"></i>`;
      }

      const zoomBtn = document.querySelector(".lg-zoom-in");
      if (zoomBtn && !zoomBtn.querySelector("i")) {
        zoomBtn.innerHTML = `<i data-lucide="zoom-in"></i>`;
      }

      const prevBtn = document.querySelector(".lg-prev");
      if (prevBtn && !prevBtn.querySelector("i")) {
        prevBtn.innerHTML = `<i data-lucide="chevron-left"></i>`;
      }

      const nextBtn = document.querySelector(".lg-next");
      if (nextBtn && !nextBtn.querySelector("i")) {
        nextBtn.innerHTML = `<i data-lucide="chevron-right"></i>`;
      }

      createIcons({ icons: { X, Maximize2, ZoomIn, ChevronLeft, ChevronRight } });
    });

  }

  async waitForImages(root) {
    const imgs = Array.from(root.querySelectorAll("img"));
    if (!imgs.length) return;
    await Promise.all(
      imgs.map(img => {
        if (img.complete && img.naturalWidth) return Promise.resolve();
        return new Promise(res => {
          img.addEventListener("load", res, { once: true });
          img.addEventListener("error", res, { once: true });
        });
      })
    );
  }

  nextFrame() {
    return new Promise(res => requestAnimationFrame(() => requestAnimationFrame(res)));
  }

  safeUpdate() {
    if (this.main) {
      this.main.updateSize();
      this.main.updateSlides();
      this.main.updateProgress();
      this.main.updateSlidesClasses();
    }
    if (this.thumbs) {
      this.thumbs.updateSize();
      this.thumbs.updateSlides();
      this.thumbs.updateProgress();
      this.thumbs.updateSlidesClasses();
    }
  }
}
